import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2 } from 'lucide-react';
import { useContasCorrentesCombo } from '../hooks/useCadastrosFinanceiros';
import type { Cartao, CartaoTaxa, SalvarCartaoRequest, TipoDebitoCredito } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: SalvarCartaoRequest) => void;
  editingCartao?: Cartao | null;
  isLoading?: boolean;
}

const defaultValues: SalvarCartaoRequest = {
  nome: '',
  tipoDebitoCredito: 0,
  isVoucher: false,
  isCombustivel: false,
  isAtiva: true,
};

export const CartaoDialog = ({ open, onOpenChange, onSubmit, editingCartao, isLoading }: Props) => {
  const form = useForm<SalvarCartaoRequest>({ defaultValues });
  const { contasCorrentes } = useContasCorrentesCombo();
  const [taxas, setTaxas] = useState<CartaoTaxa[]>([]);

  useEffect(() => {
    if (editingCartao) {
      form.reset({
        nome: editingCartao.nome,
        contaCorrenteId: editingCartao.contaCorrenteId,
        operadoraId: editingCartao.operadoraId,
        credenciadoraId: editingCartao.credenciadoraId,
        tipoDebitoCredito: editingCartao.tipoDebitoCredito,
        diaBasePagamento: editingCartao.diaBasePagamento,
        numeroIdentificacao: editingCartao.numeroIdentificacao || '',
        foneAtendimento: editingCartao.foneAtendimento || '',
        taxaTef: editingCartao.taxaTef,
        taxaAdicionalTef: editingCartao.taxaAdicionalTef,
        prazoPagamentoTef: editingCartao.prazoPagamentoTef,
        isVoucher: editingCartao.isVoucher,
        isCombustivel: editingCartao.isCombustivel,
        isAtiva: editingCartao.isAtiva,
        tipoTefId: editingCartao.tipoTefId,
        permiteFidelidade: editingCartao.permiteFidelidade ?? 0,
        taxaAntecipacaoTef: editingCartao.taxaAntecipacaoTef,
      });
      setTaxas(editingCartao.taxas ? [...editingCartao.taxas] : []);
    } else {
      form.reset(defaultValues);
      setTaxas([]);
    }
  }, [editingCartao, open, form]);

  const handleSubmit = (data: SalvarCartaoRequest) =>
    onSubmit({ ...data, taxas: taxas.filter(t => t.ateParcela > 0) });

  const addTaxa = () => setTaxas(prev => [...prev, { ateParcela: 0, taxa: 0 }]);
  const updateTaxa = (i: number, patch: Partial<CartaoTaxa>) =>
    setTaxas(prev => prev.map((t, idx) => (idx === i ? { ...t, ...patch } : t)));
  const removeTaxa = (i: number) => setTaxas(prev => prev.filter((_, idx) => idx !== i));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[540px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editingCartao ? 'Editar Cartão' : 'Novo Cartão'}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(handleSubmit)} className="space-y-4">
            <FormField control={form.control} name="nome" rules={{ required: 'Nome é obrigatório' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Nome *</FormLabel>
                  <FormControl><Input placeholder="Ex: Visa Crédito" {...field} /></FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField control={form.control} name="tipoDebitoCredito"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo</FormLabel>
                  <Select onValueChange={v => field.onChange(Number(v) as TipoDebitoCredito)} value={field.value?.toString()}>
                    <FormControl>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      <SelectItem value="0">Ambos (Débito e Crédito)</SelectItem>
                      <SelectItem value="1">Débito</SelectItem>
                      <SelectItem value="2">Crédito</SelectItem>
                    </SelectContent>
                  </Select>
                </FormItem>
              )}
            />

            <FormField control={form.control} name="contaCorrenteId"
              rules={{ required: 'Conta Corrente é obrigatória' }}
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Conta Corrente *</FormLabel>
                  <Select onValueChange={v => field.onChange(v ? Number(v) : undefined)} value={field.value?.toString()}>
                    <FormControl>
                      <SelectTrigger><SelectValue placeholder="Selecione a conta" /></SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {contasCorrentes.map(c => (
                        <SelectItem key={c.id} value={c.id.toString()}>{c.descricao}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField control={form.control} name="numeroIdentificacao"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Nº Identificação</FormLabel>
                    <FormControl><Input {...field} /></FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="diaBasePagamento"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Dia Base Pagamento</FormLabel>
                    <FormControl>
                      <Input type="number" min={1} max={31} {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="taxaTef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taxa TEF (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="taxaAdicionalTef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taxa Adicional (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="prazoPagamentoTef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Prazo (dias)</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="taxaAntecipacaoTef"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Taxa Antecip. (%)</FormLabel>
                    <FormControl>
                      <Input type="number" step="0.01" {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="tipoTefId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Cód. Tipo TEF</FormLabel>
                    <FormControl>
                      <Input type="number" {...field} value={field.value ?? ''} onChange={e => field.onChange(e.target.value ? Number(e.target.value) : undefined)} />
                    </FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="permiteFidelidade"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center rounded-lg border p-3">
                    <FormLabel className="text-sm mb-2">Fidelidade</FormLabel>
                    <FormControl><Switch checked={!!field.value} onCheckedChange={v => field.onChange(v ? 1 : 0)} /></FormControl>
                  </FormItem>
                )}
              />
            </div>

            {/* ── Taxas por faixa de parcelas ─────────────────── */}
            <div className="rounded-lg border p-3 space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-sm font-semibold">Taxas por parcela</Label>
                <Button type="button" variant="outline" size="sm" onClick={addTaxa}>
                  <Plus className="h-4 w-4 mr-1" /> Adicionar faixa
                </Button>
              </div>
              {taxas.length === 0 ? (
                <p className="text-xs text-muted-foreground">Nenhuma taxa por parcela. Ex.: até 1x → 2,5%; até 6x → 3,9%.</p>
              ) : (
                <div className="space-y-2">
                  <div className="grid grid-cols-[1fr_1fr_auto] gap-2 text-xs text-muted-foreground px-1">
                    <span>Até parcela</span><span>Taxa (%)</span><span></span>
                  </div>
                  {taxas.map((t, i) => (
                    <div key={i} className="grid grid-cols-[1fr_1fr_auto] gap-2 items-center">
                      <Input type="number" min={1} className="h-9" value={t.ateParcela || ''} onChange={e => updateTaxa(i, { ateParcela: Number(e.target.value) })} />
                      <Input type="number" step="0.01" min={0} className="h-9" value={t.taxa || ''} onChange={e => updateTaxa(i, { taxa: Number(e.target.value) })} />
                      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 text-destructive" onClick={() => removeTaxa(i)}>
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="grid grid-cols-3 gap-4">
              <FormField control={form.control} name="isVoucher"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center rounded-lg border p-3">
                    <FormLabel className="text-sm mb-2">Voucher</FormLabel>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="isCombustivel"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center rounded-lg border p-3">
                    <FormLabel className="text-sm mb-2">Combustível</FormLabel>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )}
              />
              <FormField control={form.control} name="isAtiva"
                render={({ field }) => (
                  <FormItem className="flex flex-col items-center rounded-lg border p-3">
                    <FormLabel className="text-sm mb-2">Ativo</FormLabel>
                    <FormControl><Switch checked={field.value} onCheckedChange={field.onChange} /></FormControl>
                  </FormItem>
                )}
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? 'Salvando...' : editingCartao ? 'Atualizar' : 'Criar'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};
